import React, { useState, useEffect } from 'react';
import { AuthData } from '../../provider/AuthProvider';
import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile, updatePassword, updateEmail, deleteUser } from 'firebase/auth';
import { db, auth } from '../../configs/firebase';
import { 
  FaUser, 
  FaBell, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaTrash, 
  FaSave, 
  FaEdit,
  FaGlobe,
  FaShieldAlt,
  FaDownload,
  FaCog,
  FaSignOutAlt,
  FaIdCard,
  FaBars,
  FaTimes,
  FaChevronLeft
} from 'react-icons/fa';

const Settings = () => {
  const { user, logout, updateUserProfile } = AuthData();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Form states
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    bio: ''
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    applicationUpdates: true,
    newOpportunities: true,
    weeklyDigest: false,
    marketingEmails: false
  });

  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowDirectMessages: true
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user data on component mount
  useEffect(() => {
    if (user?.profile?.personalInfo) {
      setProfileData({
        firstName: user.profile.personalInfo.firstName || '',
        lastName: user.profile.personalInfo.lastName || '',
        email: user.profile.personalInfo.email || '',
        phone: user.profile.personalInfo.phone || '',
        location: user.profile.personalInfo.location || '',
        bio: user.profile.personalInfo.bio || ''
      });
    }

    if (user?.profile?.settings) {
      setNotificationSettings(prev => ({
        ...prev,
        ...user.profile.settings.notifications
      }));
      setPrivacySettings(prev => ({
        ...prev,
        ...user.profile.settings.privacy
      }));
    }
  }, [user]);

  // Close sidebar when tab changes on mobile
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
  };

  // Show message helper
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Update profile information
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        personalInfo: {
          ...user.profile.personalInfo,
          ...profileData
        },
        updatedAt: serverTimestamp()
      };

      await updateUserProfile(user.userd.uid, updateData);
      
      await updateProfile(auth.currentUser, {
        displayName: `${profileData.firstName} ${profileData.lastName}`
      });

      showMessage('success', 'Profile updated successfully!');
    } catch (error) {
      console.error('Profile update error:', error);
      showMessage('error', 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update notification settings
  const handleNotificationUpdate = async () => {
    setLoading(true);
    try {
      const updateData = {
        settings: {
          ...user.profile.settings,
          notifications: notificationSettings
        },
        updatedAt: serverTimestamp()
      };

      await updateUserProfile(user.userd.uid, updateData);
      showMessage('success', 'Notification settings updated!');
    } catch (error) {
      showMessage('error', 'Failed to update settings.');
    } finally {
      setLoading(false);
    }
  };

  // Update privacy settings
  const handlePrivacyUpdate = async () => {
    setLoading(true);
    try {
      const updateData = {
        settings: {
          ...user.profile.settings,
          privacy: privacySettings
        },
        updatedAt: serverTimestamp()
      };

      await updateUserProfile(user.userd.uid, updateData);
      showMessage('success', 'Privacy settings updated!');
    } catch (error) {
      showMessage('error', 'Failed to update privacy settings.');
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'New passwords do not match.');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showMessage('error', 'Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(auth.currentUser, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage('success', 'Password updated successfully!');
    } catch (error) {
      showMessage('error', 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      showMessage('error', 'Please type DELETE to confirm account deletion.');
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'users', user.userd.uid));
      await deleteUser(auth.currentUser);
      
      showMessage('success', 'Account deleted successfully.');
      
      setTimeout(() => {
        logout();
      }, 2000);
    } catch (error) {
      showMessage('error', 'Failed to delete account. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Download user data
  const handleDataDownload = () => {
    const userData = {
      profile: user.profile,
      internID: user.profile?.internID,
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `oodles-data-${user.profile?.internID || 'export'}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    showMessage('success', 'Data export downloaded!');
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FaUser },
    { id: 'notifications', label: 'Notifications', icon: FaBell },
    { id: 'privacy', label: 'Privacy', icon: FaShieldAlt },
    { id: 'security', label: 'Security', icon: FaLock },
    { id: 'data', label: 'Data & Privacy', icon: FaDownload },
    { id: 'account', label: 'Account', icon: FaCog }
  ];

  const getCurrentTabLabel = () => {
    return tabs.find(tab => tab.id === activeTab)?.label || 'Settings';
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-16">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 fixed top-16 left-0 right-0 z-30">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="mr-3 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <FaBars />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{getCurrentTabLabel()}</h1>
              <p className="text-sm text-gray-600">Manage your account</p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:block py-8 px-4 sm:px-6 lg:px-8 mt-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-gray-600">Manage your account preferences and settings</p>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex flex-col w-80 max-w-xs bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <FaTimes />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-orange-100 text-orange-700 border border-orange-200'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="mr-3" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-md ${
            message.type === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="flex">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block w-64 border-r border-gray-200">
              <nav className="p-4 space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-orange-100 text-orange-700 border border-orange-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="mr-3" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 lg:p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaUser className="mr-2" />
                    Profile Information
                  </h2>
                  
                  {/* Intern ID Display */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                      <FaIdCard className="text-orange-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-800">Your Intern ID</p>
                        <p className="text-lg font-mono font-bold text-orange-600">
                          {user?.profile?.internID || 'Not assigned'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          value={profileData.firstName}
                          onChange={(e) => setProfileData(prev => ({...prev, firstName: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={profileData.lastName}
                          onChange={(e) => setProfileData(prev => ({...prev, lastName: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData(prev => ({...prev, email: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData(prev => ({...prev, phone: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={profileData.location}
                          onChange={(e) => setProfileData(prev => ({...prev, location: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bio
                      </label>
                      <div className="relative">
                        <textarea
                          value={profileData.bio}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 300) {
                              setProfileData(prev => ({...prev, bio: value}));
                            }
                          }}
                          rows={4}
                          maxLength={300}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                          placeholder="Tell us about yourself, your interests, career goals, and what you're looking for in an internship..."
                        />
                        <div className="absolute bottom-2 right-2 text-xs text-gray-500 bg-white px-2 py-1 rounded">
                          {profileData.bio.length}/300
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Help employers understand your background and career interests. 
                        {profileData.bio.length >= 280 && (
                          <span className="text-orange-600 font-medium">
                            {300 - profileData.bio.length} characters remaining
                          </span>
                        )}
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </form>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaBell className="mr-2" />
                    Notification Preferences
                  </h2>
                  
                  <div className="space-y-4">
                    {Object.entries({
                      emailNotifications: 'Email Notifications',
                      applicationUpdates: 'Application Status Updates',
                      newOpportunities: 'New Internship Opportunities',
                      weeklyDigest: 'Weekly Digest',
                      marketingEmails: 'Marketing Emails'
                    }).map(([key, label]) => (
                      <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex-1 mr-4">
                          <h3 className="font-medium">{label}</h3>
                          <p className="text-sm text-gray-600">
                            {key === 'emailNotifications' && 'Receive email notifications for important updates'}
                            {key === 'applicationUpdates' && 'Get notified when your application status changes'}
                            {key === 'newOpportunities' && 'Receive alerts for new internships matching your interests'}
                            {key === 'weeklyDigest' && 'Weekly summary of new opportunities and platform updates'}
                            {key === 'marketingEmails' && 'Promotional emails about platform features and events'}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationSettings[key]}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              [key]: e.target.checked
                            }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleNotificationUpdate}
                    disabled={loading}
                    className="mt-6 w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              )}

              {/* Privacy Tab */}
              {activeTab === 'privacy' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaShieldAlt className="mr-2" />
                    Privacy Settings
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium mb-2">Profile Visibility</h3>
                      <div className="space-y-2">
                        {[
                          { value: 'public', label: 'Public', desc: 'Anyone can view your profile' },
                          { value: 'employers-only', label: 'Employers Only', desc: 'Only verified employers can view' },
                          { value: 'private', label: 'Private', desc: 'Only you can view your profile' }
                        ].map((option) => (
                          <label key={option.value} className="flex items-start">
                            <input
                              type="radio"
                              name="profileVisibility"
                              value={option.value}
                              checked={privacySettings.profileVisibility === option.value}
                              onChange={(e) => setPrivacySettings(prev => ({
                                ...prev,
                                profileVisibility: e.target.value
                              }))}
                              className="mr-3 mt-1"
                            />
                            <div>
                              <span className="font-medium">{option.label}</span>
                              <p className="text-sm text-gray-600">{option.desc}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {Object.entries({
                        showEmail: 'Show Email Address',
                        showPhone: 'Show Phone Number',
                        allowDirectMessages: 'Allow Direct Messages'
                      }).map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <span className="font-medium">{label}</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={privacySettings[key]}
                              onChange={(e) => setPrivacySettings(prev => ({
                                ...prev,
                                [key]: e.target.checked
                              }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handlePrivacyUpdate}
                    disabled={loading}
                    className="mt-6 w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaLock className="mr-2" />
                    Security Settings
                  </h2>
                  
                  <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({...prev, currentPassword: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({...prev, newPassword: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({...prev, confirmPassword: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              )}

              {/* Data & Privacy Tab */}
              {activeTab === 'data' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaDownload className="mr-2" />
                    Data & Privacy
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium mb-2">Download Your Data</h3>
                      <p className="text-gray-600 mb-4">
                        Download a copy of all your data including profile information, applications, and activity.
                      </p>
                      <button
                        onClick={handleDataDownload}
                        className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium"
                      >
                        <FaDownload className="mr-2 inline" />
                        Download Data
                      </button>
                    </div>

                    <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                      <h3 className="font-medium mb-2 text-yellow-800">Data Usage</h3>
                      <p className="text-yellow-700 text-sm">
                        Your data is used to provide internship recommendations, connect you with employers, 
                        and improve our services. We never sell your personal information.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Tab */}
              {activeTab === 'account' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaCog className="mr-2" />
                    Account Management
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium mb-2">Sign Out</h3>
                      <p className="text-gray-600 mb-4">
                        Sign out of your account on this device.
                      </p>
                      <button
                        onClick={logout}
                        className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
                      >
                        <FaSignOutAlt className="mr-2 inline" />
                        Sign Out
                      </button>
                    </div>

                    <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                      <h3 className="font-medium mb-2 text-red-800">Delete Account</h3>
                      <p className="text-red-700 text-sm mb-4">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-red-700 mb-1">
                            Type "DELETE" to confirm:
                          </label>
                          <input
                            type="text"
                            value={deleteConfirm}
                            onChange={(e) => setDeleteConfirm(e.target.value)}
                            className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="DELETE"
                          />
                        </div>
                        
                        <button
                          onClick={handleDeleteAccount}
                          disabled={loading || deleteConfirm !== 'DELETE'}
                          className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Deleting...' : 'Delete Account'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;